import { FunctionComponent, useEffect, useState } from "react";
import { ArrowLeftRight } from "lucide-react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useAppDispatch, useAppSelector } from "../../../features/helpers";
import { INITIAL_REPASSE_FORM, RepasseCategory, RepasseForm } from "../../../models/Repasse";
import { createRepasse, setModalAddStatus } from "../../../store/RepassesSlice";
import { Modal, FormField, PillTabs } from "../../ui";

const ModalAddRepasse: FunctionComponent = () => {
  const dispatch      = useAppDispatch();
  const modalStatus   = useAppSelector((s) => s.repasses.modalAddStatus);
  const creating      = useAppSelector((s) => s.repasses.creating);
  const suppliersList = useAppSelector((s) => s.supplier.suppliersOrganization);
  const assetsList    = useAppSelector((s) => s.assets.assets);

  const [form, setForm] = useState<RepasseForm>({ ...INITIAL_REPASSE_FORM, date: new Date() });

  useEffect(() => {
    if (!modalStatus) setForm({ ...INITIAL_REPASSE_FORM, date: new Date() });
  }, [modalStatus]);

  const change = (field: keyof RepasseForm, value: any) =>
    setForm((p) => ({ ...p, [field]: value }));

  const isValid = () =>
    !!form.category && !!form.supplier_id && !!form.asset_id && Number(form.value) > 0 && !!form.date;

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValid() || creating) return;
    dispatch(createRepasse(form));
  };

  const close = () => { if (!creating) dispatch(setModalAddStatus(false)); };
  const selectedAsset = assetsList?.find((a: any) => a.id === Number(form.asset_id));

  return (
    <Modal
      open={modalStatus}
      onClose={close}
      icon={ArrowLeftRight}
      title="Adicionar repasse"
      subtitle="envio (pagamento) ou recebimento entre empresa e fornecedor"
      maxWidth={620}
      footer={
        <>
          <button type="button" className="btn-pill ghost" onClick={close} disabled={creating}>Cancelar</button>
          <button type="submit" form="modal-add-repasse-form" className="btn-pill primary" disabled={!isValid() || creating}>
            {creating ? "Salvando..." : "Adicionar repasse"}
          </button>
        </>
      }
    >
      <form id="modal-add-repasse-form" onSubmit={submit}>

        <PillTabs
          tabs={[
            { key: "envio", label: "Envio (pagamento)" },
            { key: "recebimento", label: "Recebimento" },
          ]}
          value={form.category}
          onChange={(k) => change("category", k as RepasseCategory)}
          ariaLabel="Tipo de repasse"
        />

        <div className="form-row">
          <FormField label="Fornecedor" htmlFor="r-supplier">
            <select id="r-supplier" value={form.supplier_id} onChange={(e) => change("supplier_id", e.target.value)}>
              <option value="">Selecione o fornecedor</option>
              {suppliersList?.map((s: any) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </FormField>
          <FormField label="Asset" htmlFor="r-asset">
            <select id="r-asset" value={form.asset_id} onChange={(e) => change("asset_id", e.target.value)}>
              <option value="">Selecione o asset</option>
              {assetsList?.map((a: any) => <option key={a.id} value={a.id}>{a.name} ({a.symbol})</option>)}
            </select>
          </FormField>
        </div>

        <div className="form-row">
          <FormField label={`Valor${selectedAsset ? ` (${selectedAsset.symbol})` : ""}`} htmlFor="r-value">
            <input id="r-value" type="number" step="0.00000001" placeholder="0,00" value={form.value} onChange={(e) => change("value", e.target.value)} />
          </FormField>
          <FormField label="Data" htmlFor="r-date">
            <DatePicker id="r-date" selected={form.date} onChange={(d: any) => change("date", d)} dateFormat="dd/MM/yyyy" placeholderText="Selecione a data" maxDate={new Date()} />
          </FormField>
        </div>

        <p className="info-text">
          {form.category === "envio"
            ? <>Envio: debita a carteira da empresa <strong>e</strong> a do fornecedor no asset escolhido.</>
            : <>Recebimento: credita a carteira do fornecedor <strong>e</strong> a da empresa no asset escolhido.</>}
        </p>
      </form>
    </Modal>
  );
};

export default ModalAddRepasse;
